import { LinkButtonComponent } from '@/app/shared/components/link-button/link-button.component';
import { WidgetHeaderComponent } from '@/app/shared/components/widget-header/widget-header.component';
import { DemandSiteViewModel, MyDemandViewModel } from '@/app/models/com/demand.model';
import { DsyListCustomConf, DsyListElementConf, DsyTagConfig } from '@/app/models/dsy-drop-down.model';
import { DsyRowData, DsyTableConf } from '@/app/models/dsy-table.model';
import { TrendsWidget } from '@/app/models/widget.model';
import { DisplayLocalePipe } from '@/app/pipes/display-locale.pipe';
import { DemandService } from '@/app/services/demand.service';
import { ExternalUrlsService } from '@/app/services/external-urls.service';
import { UserService } from '@/app/services/user.service';
import { EmptyStateComponent } from '@/app/shared/components/empty-state/empty-state.component';
import { applyCustomDemandsCss, clearDemandsInterval } from '@/app/shared/format-styles/mydemand-style';
import { environment } from '@/environments/environment';
import { NgClass } from '@angular/common';
import { AfterViewChecked, Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-shared-services';
import { TooltipModule } from 'primeng/tooltip';
import { MixPanelEvent, MixPanelService } from '@/app/services/mixpanel.service';
import {resizeDsyTableSortIcons} from '@/app/shared/format-styles/dsyTable-style'
import { DemandDatePipe } from '@/app/pipes/demand-date.pipe';

@Component({
  selector: 'dr-flex-home-ui-app-demand-widget',
  templateUrl: './demand-widget.component.html',
  styleUrl: './demand-widget.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  standalone: true,
  imports: [DisplayLocalePipe, MatProgressBarModule, NgClass, LinkButtonComponent, EmptyStateComponent, TranslateModule, WidgetHeaderComponent, TooltipModule,DemandDatePipe],
})
export class DemandWidgetComponent implements OnInit, OnDestroy, AfterViewChecked {
  widget = input.required<TrendsWidget>();

  // MARK: Injectable
  private readonly demandService = inject(DemandService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cookieService = inject(CookieService);
  private readonly userService = inject(UserService);
  private readonly mixpanelService = inject(MixPanelService);
  private readonly translate = inject(TranslateService);

  // MARK: Properties
  readonly mixPanelEvent = MixPanelEvent;
  private readonly displayLocalePipe = new DisplayLocalePipe();
  private readonly maxDropDownDisplayCharacters = 29;
  private readonly dsyDropDownListConfig: DsyListCustomConf = {
    minWidth: '25rem',
    maxWidth: '30rem',
    maxHeight: '13.5rem',
    status: 'default',
    border: false,
    disabled: false,
    exclusiveSubrowVisibility: true,
    reverseRender: false,
    isNavBarStyle: false,
  };
  readonly maxSelections = 5;
  //maxSelectionWarning = signal(false);
  readonly dropDownTagConfig: DsyTagConfig = {
    variant: 'secondary',
  };

  readonly trendsUrl = inject(ExternalUrlsService).trends;

  readonly columnsDefinition = [
    {
      key: 'site',
      header: 'FLEX_HOME.COM_WIDGET.DEMAND.COLUMNS.SITE',
      // This function cleanly extracts the data for a cell
      dataAccessor: (demandSite: DemandSiteViewModel) => demandSite.name[0]?.label || '',
      isTime: false,
      isSortable: false,
    },
    {
      key: 'min',
      header: 'FLEX_HOME.COM_WIDGET.DEMAND.COLUMNS.MIN',
      dataAccessor: (demandSite: DemandSiteViewModel) => {
        // Show '-' if null/undefined, otherwise convert number to string
        return demandSite.min == null ? '-' : String(demandSite.min);
      },
      isTime: false,
      isSortable: false,
    },
    {
      key: 'max',
      header: 'FLEX_HOME.COM_WIDGET.DEMAND.COLUMNS.MAX',
      dataAccessor: (demandSite: DemandSiteViewModel) => {
        return demandSite.max == null ? '-' : String(demandSite.max);
      },
      isTime: false,
      isSortable: false,
    },
    {
      key: 'average',
      header: 'FLEX_HOME.COM_WIDGET.DEMAND.COLUMNS.AVERAGE',
      dataAccessor: (demandSite: DemandSiteViewModel) => {
        return demandSite.average == null ? '-' : String(demandSite.average);
      },
      isTime: true,
      isSortable: false,
    },
    {
      key: 'demand',
      header: 'FLEX_HOME.COM_WIDGET.DEMAND.COLUMNS.DEMAND_SHARE',
      dataAccessor: (demandSite: DemandSiteViewModel) => {
        // UNCOMMENT FOR DEBUGGING PURPOSES ONLY
        //console.log('site.demand in template row:', demandSite.demand, typeof demandSite.demand);
        return demandSite.demand === null ? '-' : String(demandSite.demand);
      },
      isTime: false,
      isSortable: true,
    },
  ];

  // MARK: Signals
  readonly myDemand = signal<MyDemandViewModel | null>(null);

  readonly sitesDropDownList = signal<DsyListElementConf[]>([]);

  readonly selectedSites = computed<DemandSiteViewModel[]>(() => {
    const sites = this.sitesDropDownList();
    const myDemand = this.myDemand();
    return this.getSelectedSites(sites, myDemand);
  });

  // Generate the table data dynamically from the column definitions.
  readonly tableData = computed<DsyRowData[]>(() => {
    const selectedSites = this.selectedSites();
    return this.getTableData(selectedSites);
  });

  // Generate the table configuration dynamically.
  readonly tableConf = computed<DsyTableConf>(() => {
    const rowOrder = this.tableData().map((row) => row.id);
    const columnOrder = this.columnsDefinition.map((col) => col.key);
    return this.getTableConfig(rowOrder, columnOrder);
  });

  readonly total = computed<number | null>(() => {
    const sites = this.selectedSites(); // Reuse the existing computed
    return this.getTotal(sites);
  });

  readonly showDropDownTag = computed<boolean>(() => {
    const selectedSites = this.selectedSites();
    return this.getShowDropDownTag(selectedSites);
  });

  readonly dropDownListConfig = computed<DsyListCustomConf>(() => {
    const sitesLength = this.sitesDropDownList().length;
    return {
      ...this.dsyDropDownListConfig,
      itemsToDisplay: sitesLength,
    };
  });
  readonly locale = signal('');
  readonly timezone = signal('');
  readonly sitesDropDownEntries = computed(() => {
    const selectedCount = this.selectedSites().length;
    const remaining = this.maxSelections - selectedCount;

    const message = 
      remaining <= 0
        ? this.translate.instant('FLEX_HOME.COM_WIDGET.DEMAND.MAX_SELECTED')
        :  this.translate.instant('FLEX_HOME.COM_WIDGET.DEMAND.SELECT_ANY',{remaining});

    return [
      {
        id: 'info',
        labelContent: message,
        hasRightCheckbox: false,
      } as DsyListElementConf,
      ...this.sitesDropDownList(),
    ];
  });
  // OnInit
  ngOnInit(): void {
    this.locale.set(this.cookieService.getI18NLocale() || environment.defaults.locale);
    this.fetchUserTimezone();
    this.fetchLatestDemand();
  }
  ngAfterViewChecked() {
    
    setTimeout(() => applyCustomDemandsCss(), 500);
    resizeDsyTableSortIcons();
    
  }
  ngOnDestroy(): void {
    clearDemandsInterval();
  }
  // MARK: Methods

  private fetchLatestDemand() {
    this.demandService
      .getLatestDemand()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((demand) => {
        const sortedSites = this.sortSites(demand.sites);
        this.myDemand.set({ ...demand, sites: sortedSites });
        this.setSites({ ...demand, sites: sortedSites });
      });
  }

  private fetchUserTimezone() {
    this.userService
      .getUserPreferences()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((preferences) => {
        const userTimezone = preferences?.timezone;
        if (userTimezone) {
          this.timezone.set(userTimezone);
        }
      });
  }

  // Sort the sites by their display name
  private sortSites(sites: DemandSiteViewModel[]): DemandSiteViewModel[] {
    return [...sites].sort((a, b) => {
      // 1. Prioritize sites with demand data (highest demand first)
      const aHasData = a.demand > 0 || a.min != null || a.max != null || a.average != null;
      const bHasData = b.demand > 0 || b.min != null || b.max != null || b.average != null;

      if (aHasData !== bHasData) return aHasData ? -1 : 1;

      // 2. Within data sites, highest demand first
      if (aHasData && bHasData) return b.demand - a.demand;

      // 3. Alphabetical fallback
      const aName = this.displayLocalePipe.transform(a.name, null, 'label');
      const bName = this.displayLocalePipe.transform(b.name, null, 'label');
      return aName.toLowerCase().localeCompare(bName.toLowerCase());
    });
  }

  private setSites(myDemand: MyDemandViewModel | null) {
    if (myDemand) {
      const dropdownSites = myDemand.sites;
      this.sitesDropDownList.set(
        dropdownSites.map(
          (site, index) =>
            ({
              id: site.id,
              labelContent: site.name[0].label,
              density: 'wide',
              isSelected: index < Math.min(3, myDemand.sites.length),
              hasRightCheckbox: true,
              rightCheckboxSize: 'medium',
              hasChildren: true,
              onListElementClick: () => this.handleSiteClick(site.id),
            }) as DsyListElementConf
        )
      );
    }
  }

  // Handle site click events on dropdown to enable or disable for more than 5 Sites selected
  private handleSiteClick(clickedSiteId: string): void {
    const currentSites = this.sitesDropDownList();
    const selectedCount = currentSites.filter((s) => s.isSelected).length;

    const needsRevert = selectedCount > this.maxSelections;
    //this.maxSelectionWarning.set(needsRevert);
    const finalSelectedCount = needsRevert ? this.maxSelections : selectedCount;

    const finalSites = currentSites.map((site) => {
      let isSelected = site.isSelected;

      // First, determine the final selection state for this item.
      // If a revert is needed and this is the item that was just clicked, undo its selection.
      if (needsRevert && site.id === clickedSiteId) {
        isSelected = false;
      }

      // Next, determine the final cursor state based on the corrected selection count.
      let customCursor = 'pointer'; // Default for clickable items
      if (finalSelectedCount >= this.maxSelections && !isSelected) {
        customCursor = 'default';
      }

      // If nothing changed for this specific site, return the original object to prevent unnecessary re-renders.
      if (site.isSelected === isSelected && site.customCursor === customCursor) {
        return site;
      }

      return {
        ...site,
        isSelected,
        customCursor,
      };
    });

    this.sitesDropDownList.set(finalSites);
  }

  private getSelectedSites(sites: DsyListElementConf[], myDemand: MyDemandViewModel | null): DemandSiteViewModel[] {
    if (sites.length === 0 || !myDemand) {
      return [];
    }

    const selectedSites = sites.filter((site) => site.isSelected === true).map((s) => s.id);

    this.mixpanelService.trackEvent(MixPanelEvent.DROPDOWN_DEMAND, { sites: selectedSites });

    return myDemand.sites.filter((site) => selectedSites.includes(site.id));
  }

  private getTableData(selectedSites: DemandSiteViewModel[]): DsyRowData[] {
    if (!selectedSites) return [];

    return selectedSites.map(
      (offer, index) =>
        ({
          id: `offer-row-${index}`,
          // Create the cells for each row
          cells: Object.fromEntries(
            this.columnsDefinition.map((col) => [
              col.key,
              {
                id: `cell-${index}-${col.key}`,
                property: col.key,
                value: col.dataAccessor(offer),
              },
            ])
          ),
        }) as DsyRowData
    );
  }

  private getTableConfig(rowOrder: string[], columnOrder: string[]): DsyTableConf {
    return {
      styling: {
        table: {
          areRowsSeparated: true,
          density: 'default',
        },
        columns: {
            minWidth: '0px',
          },

      },
      renderOrder: {
        columns: {
          defaultOrder: columnOrder,
        },
        rows: {
          defaultOrder: rowOrder,
        },
      },
      headerRow: {
        id: 'main-header',
        cells: Object.fromEntries(
          this.columnsDefinition.map((col) => [
            col.key,
            {
              id: `header-${col.key}`,
              property: col.key,
              value: col.header,
              isSortable: col.isSortable,
            },
          ])
        ),
      },
    };
  }

  private getTotal(sites: DemandSiteViewModel[]): number | null {
    if (!sites || sites.length === 0) {
      return null;
    }

    return sites.reduce((total, site) => total + (site.demand || 0), 0);
  }

  private getShowDropDownTag(selectedSites: DemandSiteViewModel[]): boolean {
    if (selectedSites.length === 0) return false;
    let nameSum = selectedSites.reduce((sum, site) => {
      const name = this.displayLocalePipe.transform(site.name, null, 'label');
      return sum + name.length;
    }, 0);

    //add 2 based on the space and the comma
    nameSum += 2 * (selectedSites.length - 1);

    return nameSum > this.maxDropDownDisplayCharacters;
  }
}
